import { Countdown, LiveRegion, countdownTone, useTranslate, type CountdownTone } from '@athena/ui'
import { useEffect, useRef, useState } from 'react'

import { useFormat } from '../../lib/format.js'

export interface AttemptTimerProps {
  /**
   * How long the server said was left, and when the client learned it.
   *
   * Not a deadline the browser subtracts its own clock from. A student who
   * puts their laptop's clock forward an hour changes `Date.now()` and changes
   * nothing about this number, because it arrived already computed.
   */
  secondsRemaining: number
  /**
   * Changes whenever a fresh answer arrives from the server, even when the
   * number happens to be the same. Reseeding the local countdown is keyed on
   * this rather than on the number, because two consecutive reads a second
   * apart can legitimately return the same integer.
   */
  receivedAt: number
  /** Run once, when the local countdown reaches zero. Refetching is the caller's job. */
  onExpire: () => void
}

/**
 * The countdown on a timed practice.
 *
 * Three rules, and they are the whole component:
 *
 * **The server's number is the truth.** Every render seeds from
 * `secondsRemaining`, which the API computed. Nothing here reads a deadline and
 * subtracts the device's clock from it, so moving the system time does nothing.
 *
 * **The local tick is display only.** Between requests the countdown runs on
 * `performance.now()`, which is monotonic and immune to a clock change, and it
 * only ever counts *down* from what the server last said. When a new answer
 * arrives — a refetch, a save, a submission, coming back to the tab — it
 * replaces the local estimate outright, whether that is more time or less.
 *
 * **Nothing is announced every second.** The clock's value is on the element as
 * an accessible name, which a screen-reader user can read when they choose to.
 * What is announced is a change of state: entering the warning, entering the
 * last minute, and time being up. Three interruptions in a practice, not one a
 * second.
 */
export function AttemptTimer({ secondsRemaining, receivedAt, onExpire }: AttemptTimerProps) {
  const t = useTranslate()
  const format = useFormat()

  const [seconds, setSeconds] = useState(secondsRemaining)
  const [announcement, setAnnouncement] = useState('')
  /** The last tone announced, so a state is announced once rather than every tick. */
  const announced = useRef<CountdownTone | null>(null)
  /** So the caller is told once, even though several ticks can see zero. */
  const expired = useRef(false)

  // Reseed from the server, and start the monotonic clock again from here.
  // Keyed on `receivedAt` so an unchanged number still counts as fresh news.
  useEffect(() => {
    const base = performance.now()
    setSeconds(secondsRemaining)

    const id = setInterval(() => {
      const elapsed = (performance.now() - base) / 1000
      setSeconds(Math.max(0, Math.round(secondsRemaining - elapsed)))
    }, 1000)

    return () => clearInterval(id)
  }, [secondsRemaining, receivedAt])

  const tone = countdownTone(seconds)

  // Announce a change of state, and only that.
  useEffect(() => {
    if (announced.current === tone) return
    // The first tone a student sees is not news — they have just started the
    // practice and the dialog told them how long they had.
    const first = announced.current === null
    announced.current = tone
    if (first) return

    if (tone === 'expired') setAnnouncement(t('attempt.timer.expiredAnnouncement'))
    else if (tone === 'critical')
      setAnnouncement(t('attempt.timer.criticalAnnouncement', [format.remainingWords(seconds)]))
    else if (tone === 'warning')
      setAnnouncement(t('attempt.timer.warningAnnouncement', [format.remainingWords(seconds)]))
    // `seconds` is deliberately out of the dependency list: this effect is
    // about the tone changing, and depending on the number would run it every
    // tick to decide not to announce anything.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tone, t])

  useEffect(() => {
    if (seconds > 0 || expired.current) return
    expired.current = true
    onExpire()
  }, [seconds, onExpire])

  return (
    <>
      <Countdown
        seconds={seconds}
        label={
          seconds <= 0 ? t('attempt.timer.expiredAnnouncement') : format.timeRemaining(seconds)
        }
        expiredLabel={t('attempt.timer.expired')}
      />
      <LiveRegion message={announcement} />
    </>
  )
}
